import React from 'react';
import { cn } from '@/lib/utils';

export function PageHeader({ title, subtitle, actions, badge, testId }) {
  return (
    <div className="mb-8 flex min-w-0 flex-col items-start justify-between gap-5 sm:flex-row sm:items-end" data-testid={testId}>
      <div className="min-w-0">
        {badge && (
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-lime-500 mb-2">
            {badge}
          </div>
        )}
        <h1 className="font-display text-3xl font-black tracking-tighter text-zinc-50 md:text-4xl">
          {title}
        </h1>
        {subtitle && (
          <p className="text-sm text-zinc-400 mt-2 max-w-xl">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">{actions}</div>}
    </div>
  );
}

export function Surface({ children, className, ...rest }) {
  return (
    <div
      className={cn(
        'bg-zinc-950 border border-zinc-700 rounded-sm',
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Button({ as: As = 'button', variant = 'primary', size = 'md', className, children, ...rest }) {
  const variants = {
    primary: 'bg-lime-500 hover:bg-lime-400 text-zinc-950 font-semibold',
    secondary: 'bg-zinc-900 hover:bg-zinc-800 text-zinc-100 border border-zinc-700',
    ghost: 'text-zinc-300 hover:text-zinc-50 hover:bg-zinc-900',
    danger: 'bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30',
  };
  const sizes = {
    sm: 'h-8 px-3 text-xs',
    md: 'h-9 px-4 text-sm',
    lg: 'h-11 px-6 text-sm',
    icon: 'h-9 w-9',
  };
  return (
    <As
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-sm transition-colors duration-150 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none',
        variants[variant],
        sizes[size],
        className
      )}
      {...rest}
    >
      {children}
    </As>
  );
}

export const Input = React.forwardRef(function Input({ className, ...rest }, ref) {
  return (
    <input
      ref={ref}
      className={cn(
        'w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-sm px-3 py-2 rounded-sm font-mono',
        'placeholder:text-zinc-500 focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500/30',
        className
      )}
      {...rest}
    />
  );
});

export function Textarea({ className, ...rest }) {
  return (
    <textarea
      className={cn(
        'w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-sm px-3 py-2 rounded-sm font-mono',
        'placeholder:text-zinc-500 focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500/30',
        className
      )}
      {...rest}
    />
  );
}

export function Label({ children, className, htmlFor }) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn(
        'block text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-400 mb-1.5',
        className
      )}
    >
      {children}
    </label>
  );
}

function localDateTimeValue(date) {
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

export function DateTimePicker({ value, onChange, testId }) {
  const inputRef = React.useRef(null);
  const setFromDate = (date) => onChange(localDateTimeValue(date));
  const openPicker = () => {
    const input = inputRef.current;
    if (input?.showPicker) input.showPicker();
    else input?.focus();
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          ref={inputRef}
          type="datetime-local"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
        <Button type="button" variant="secondary" onClick={openPicker} className="shrink-0">
          Selecionar
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => setFromDate(new Date(Date.now() + 60 * 60 * 1000))} className="text-xs font-mono text-zinc-400 hover:text-lime-300">
          +1h
        </button>
        <button
          type="button"
          onClick={() => {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(9, 0, 0, 0);
            setFromDate(tomorrow);
          }}
          className="text-xs font-mono text-zinc-400 hover:text-lime-300"
        >
          amanhã 09h
        </button>
        {value && (
          <button type="button" onClick={() => onChange('')} className="text-xs font-mono text-zinc-500 hover:text-red-300">
            limpar
          </button>
        )}
      </div>
    </div>
  );
}

export function Badge({ children, variant = 'default', className, ...rest }) {
  const variants = {
    default: 'text-zinc-400 bg-zinc-900 border-zinc-700',
    success: 'text-emerald-400 bg-emerald-400/10 border-emerald-500/20',
    danger: 'text-red-400 bg-red-400/10 border-red-500/20',
    warn: 'text-amber-400 bg-amber-400/10 border-amber-500/20',
    info: 'text-blue-400 bg-blue-400/10 border-blue-500/20',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.12em] border rounded-sm',
        variants[variant],
        className
      )}
      {...rest}
    >
      {children}
    </span>
  );
}

export function EmptyState({ icon: Icon, title, hint, action }) {
  return (
    <div className="border border-dashed border-zinc-700 rounded-sm p-10 text-center">
      {Icon && <Icon size={32} className="mx-auto text-zinc-500 mb-3" />}
      <h3 className="font-display font-bold text-lg text-zinc-300">{title}</h3>
      {hint && <p className="text-sm text-zinc-400 mt-1">{hint}</p>}
      {action && <div className="mt-4 inline-block">{action}</div>}
    </div>
  );
}

export function Spinner({ size = 16, className }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={cn('animate-spin', className)}
      fill="none"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function Modal({ open, onClose, title, children, footer, testId, panelClassName }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={testId}
    >
      <div
        className={cn(
          'bg-zinc-950 border border-zinc-700 rounded-sm max-w-lg w-full max-h-[90vh] overflow-y-auto',
          panelClassName
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-zinc-700 flex items-center justify-between">
          <h2 className="font-display font-bold text-lg text-zinc-50">{title}</h2>
          <button
            onClick={onClose}
            data-testid="modal-close-btn"
            className="text-zinc-400 hover:text-zinc-100 text-xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && (
          <div className="px-5 py-4 border-t border-zinc-700 flex items-center justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function Toast({ tone = 'info', message, onDone, duration = 3500 }) {
  React.useEffect(() => {
    if (!message) return;
    const t = setTimeout(onDone, duration);
    return () => clearTimeout(t);
  }, [message, onDone, duration]);
  if (!message) return null;
  const tones = {
    success: 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10',
    error: 'border-red-500/40 text-red-300 bg-red-500/10',
    info: 'border-zinc-700 text-zinc-200 bg-zinc-900',
  };
  return (
    <div
      data-testid="toast"
      className={cn(
        'fixed z-[9999] bottom-20 right-6 max-w-md border rounded-sm px-4 py-3 text-sm font-mono shadow-2xl stagger-in',
        tones[tone]
      )}
    >
      {message}
    </div>
  );
}

export function useToast() {
  const [t, setT] = React.useState({ tone: 'info', message: '' });
  const show = (message, tone = 'info') => setT({ tone, message });
  const node = <Toast tone={t.tone} message={t.message} onDone={() => setT({ tone: 'info', message: '' })} />;
  return { show, node };
}
